import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ReportPageWrapper, { StatCard } from "./ReportPageWrapper";
import {
  PiggyBank,
  DollarSign,
  Calendar,
  TrendingUp,
  BarChart3,
  ArrowUp,
  Download,
  Printer,
  Search,
  RotateCcw,
  FileText,
  Eye,
} from "lucide-react";
import { format, subMonths } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-slate-700 mb-1">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} style={{ color: entry.color }} className="text-xs">
            {entry.name}: {entry.value?.toLocaleString()} FRW
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function SavingsReport() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  const { data: contributions, isLoading } = useQuery(
    ["my-contributions", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=1000");
      return Array.isArray(data.data) ? data.data : [];
    },
    { enabled: !!user?.id }
  );

  const { data: memberPaymentsData } = useQuery(
    ["my-savings-report-payments", user?.id],
    async () => {
      const { data } = await api.get("/member-payments/my");
      return (data.data || []) as any[];
    },
    { enabled: !!user?.id }
  );

  const approvedSavingsAmount = useMemo(() => {
    return (memberPaymentsData || [])
      .filter((p: any) => p.type === "saving" && String(p.status || "").toLowerCase() === "approved")
      .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
  }, [memberPaymentsData]);

  const allData = contributions || [];

  const currentBalance = useMemo(() =>
    allData.reduce((s: number, c: any) => s + (parseFloat(c.amount || c.depositAmount) || 0), 0) + approvedSavingsAmount,
  [allData, approvedSavingsAmount]);

  const yearlyTotal = useMemo(() => {
    const now = new Date();
    const contribYear = allData
      .filter((c: any) => new Date(c.contributionDate || c.createdAt).getFullYear() === now.getFullYear())
      .reduce((s: number, c: any) => s + (parseFloat(c.amount || c.depositAmount) || 0), 0);
    const savingsYear = (memberPaymentsData || [])
      .filter((p: any) => p.type === "saving" && String(p.status || "").toLowerCase() === "approved" && new Date(p.paymentDate || p.submittedAt || p.createdAt).getFullYear() === now.getFullYear())
      .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
    return contribYear + savingsYear;
  }, [allData, memberPaymentsData]);

  const filtered = useMemo(() => {
    return allData.filter((c: any) => {
      const d = new Date(c.contributionDate || c.createdAt);
      const matchMonth = monthFilter === "all" || d.getMonth() === parseInt(monthFilter);
      const matchYear = yearFilter === "all" || d.getFullYear() === parseInt(yearFilter);
      const matchSearch = !search ||
        (c.referenceNumber || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.paymentMethod || "").toLowerCase().includes(search.toLowerCase());
      return matchMonth && matchYear && matchSearch;
    });
  }, [allData, monthFilter, yearFilter, search]);

  const monthlyAvg = useMemo(() => {
    const months = new Set(allData.map((c: any) => {
      const d = new Date(c.contributionDate || c.createdAt);
      return `${d.getFullYear()}-${d.getMonth()}`;
    }));
    return months.size > 0 ? currentBalance / months.size : 0;
  }, [allData, currentBalance]);

  const highestMonth = useMemo(() => {
    const months: Record<string, number> = {};
    allData.forEach((c: any) => {
      const d = new Date(c.contributionDate || c.createdAt);
      const key = format(d, "MMM yy");
      months[key] = (months[key] || 0) + (parseFloat(c.amount || c.depositAmount) || 0);
    });
    const entries = Object.entries(months);
    return entries.length ? Math.max(...entries.map(([, v]) => v)) : 0;
  }, [allData]);

  const thisMonth = useMemo(() => {
    const now = new Date();
    return allData
      .filter((c: any) => {
        const d = new Date(c.contributionDate || c.createdAt);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((s: number, c: any) => s + (parseFloat(c.amount || c.depositAmount) || 0), 0);
  }, [allData]);

  const monthlyTrend = useMemo(() => {
    const months: Record<string, number> = {};
    allData.forEach((c: any) => {
      const d = new Date(c.contributionDate || c.createdAt);
      const key = format(d, "MMM yy");
      months[key] = (months[key] || 0) + (parseFloat(c.amount || c.depositAmount) || 0);
    });
    const now = new Date();
    const result: { label: string; savings: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(now, i);
      const key = format(d, "MMM yy");
      result.push({ label: key, savings: months[key] || 0 });
    }
    return result;
  }, [allData]);

  const formatCurrency = (val: number) => (val || 0).toLocaleString() + " FRW";

  const handleReset = () => { setSearch(""); setMonthFilter("all"); setYearFilter("all"); };

  const handleExport = () => {
    const headers = ["Date", "Amount", "Payment Method", "Reference", "Status", "Verified By"];
    const rows = filtered.map((c: any) => [
      format(new Date(c.contributionDate || c.createdAt), "yyyy-MM-dd"),
      parseFloat(c.amount || c.depositAmount) || 0,
      c.paymentMethod || "N/A", c.referenceNumber || "N/A",
      c.isVerified ? "Verified" : c.status || "Pending", c.verifiedBy || "N/A",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `savings-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl" />)}
        </div>
        <div className="h-64 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  return (
    <ReportPageWrapper
      title="Savings Report"
      breadcrumbItems={[
        { label: "Dashboard", path: "/member/dashboard" },
        { label: "Member Reports", path: "/member/reports" },
        { label: "Savings Report" },
      ]}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Current Balance" value={formatCurrency(currentBalance)} icon={PiggyBank} color="#009A44" />
        <StatCard label="Total Saved" value={formatCurrency(currentBalance)} icon={DollarSign} color="#3B82F6" />
        <StatCard label="This Month" value={formatCurrency(thisMonth)} icon={Calendar} color="#8B5CF6" />
        <StatCard label="Year Total" value={formatCurrency(yearlyTotal)} icon={TrendingUp} color="#EAB308" />
        <StatCard label="Monthly Average" value={formatCurrency(monthlyAvg)} icon={BarChart3} color="#6366F1" />
        <StatCard label="Highest Month" value={formatCurrency(highestMonth)} icon={ArrowUp} color="#009A44" />
      </div>

      {/* Chart */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-[#009A44]" />
          Monthly Savings Trend
        </h3>
        {monthlyTrend.some(d => d.savings > 0) ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="savings" name="Savings" fill="#009A44" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-10 bg-slate-50 rounded-lg">
            <BarChart3 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-500">No savings data yet</p>
            <p className="text-xs text-slate-400 mt-1">Start saving to see your monthly trends.</p>
          </div>
        )}
      </div>

      {/* Savings Table Card */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search savings..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            <select
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className="h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              <option value="all">All Months</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>{format(new Date(2024, i), "MMMM")}</option>
              ))}
            </select>
            <select
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
              className="h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              <option value="all">All Years</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-500 h-9">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#009A44]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Method</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Reference</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Verified By</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.slice(0, 50).map((c: any, idx: number) => (
                <tr key={c.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {format(new Date(c.contributionDate || c.createdAt), "MMM dd, yyyy")}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {(parseFloat(c.amount || c.depositAmount) || 0).toLocaleString()} FRW
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.paymentMethod || "N/A"}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs max-w-[100px] truncate">
                    {c.referenceNumber || "N/A"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={c.isVerified ? "success" : c.status === "Rejected" ? "destructive" : "secondary"}
                      className="text-[10px]"
                    >
                      {c.isVerified ? "Verified" : c.status || "Pending"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.verifiedBy || "N/A"}</td>
                  <td className="px-4 py-3 text-center">
                    {c.documentReceipt || c.bankReceipt ? (
                      <a href={c.documentReceipt || c.bankReceipt} target="_blank" rel="noopener noreferrer">
                        <Eye className="h-3.5 w-3.5 text-blue-500 inline cursor-pointer" />
                      </a>
                    ) : (
                      <span className="text-slate-300 text-xs">--</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                    No savings records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ReportPageWrapper>
  );
}
